// src/pages/Invoices/GeneratedInvoice.jsx
import { Box, Grid, Typography, Button, CircularProgress } from '@mui/material';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import axios from 'axios';
import Header from './Header';
import styles from './index.module.css';
import Invoice from './Invoice';
import { Container } from '@mui/material';
import Footer from './Footer';
import { useParams } from 'react-router-dom';
import { validatePublicInvoiceFinancials } from '../../utils/publicInvoiceFinancials';
import { getPublicInvoice } from '../../services/invoices';
import { useProjectTemplateConfig } from '../../hooks/useProjectTemplateConfig';
import InvoiceDetails from './InvoiceDetails/index';
import TotalContainer from './TotalContainer/index.js';
import AdjustmentContainer from './AdjustmentContainer/index.js';
import toast from 'react-hot-toast';
import Watermark from './WaterMark';
import { toCents, fromCents } from '../../utils/totals';
import { resolveAdditionalCostFlags } from '../../utils/pricingEngine';
import { fetchLatestCompletedSalesOrderForOrder } from '../../services/estimation.js';
import { computeTotalsFromSO } from '../../utils/totals';
import { buildInvoiceTaxBreakdown } from '../../utils/taxBreakdown';
import { buildInvoiceDisplayPlan } from '../../utils/invoiceLineDisplay';
import { resolveOtherServiceCustomerAmounts } from '../../utils/otherServicePricing';
import { isDepositInvoiceRole } from '../../utils/invoiceDeposits';
import InvoicePrintActions, { printInvoice } from './InvoicePrintActions';
import { AuthContext } from '../../helpers/AuthContext';

// Normalize address values from API payload; avoid placeholder/random fallbacks.
const normalizeAddress = (addr) => {
  if (!addr || addr === '1' || addr === 1) return null;
  if (typeof addr === 'string') {
    try {
      const parsed = JSON.parse(addr);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof addr === 'object' ? addr : null;
};

const parseRel = (rel) => {
  if (!rel) return {};
  if (typeof rel === 'string') {
    try { return JSON.parse(rel); } catch { return {}; }
  }
  return rel;
};

const applyInvoiceDefaults = (raw) => {
  const d = raw ?? {};

  const items = Array.isArray(d.items) ? d.items : [];
  const normalizedItems = items.map((it) => ({
    ...it,
    // normalize shapes so calculator is resilient
    pricings: Array.isArray(it.pricings) ? it.pricings : [],
    artworks:
      Array.isArray(it.ArtWorks) && it.ArtWorks.length
        ? it.ArtWorks
        : Array.isArray(it.artworks)
        ? it.artworks
        : [],
    decorations: Array.isArray(it.decorations) ? it.decorations : [],
    taxDetails: {
      // prefer per-item rate if present; fallback later to invoice-level
      rate:
        it?.taxDetails?.rate != null
          ? Number(it.taxDetails.rate) || 0
          : null,
      countryCode: it?.taxDetails?.countryCode || undefined,
      stateCode: it?.taxDetails?.stateCode || undefined,
    },
  }));

  const otherServices = Array.isArray(d.otherServices)
    ? d.otherServices
    : Array.isArray(d.otherService)
    ? d.otherService
    : [];

  const isDepositInvoice = isDepositInvoiceRole(d);
  const amountNum = Number(d?.amount);
  const hasDetailedRows = normalizedItems.length > 0 || (otherServices || []).some((svc) => {
    if (!svc || typeof svc !== 'object') return false;
    const total = Number(svc?.Total ?? svc?.total ?? 0);
    const unitCost = Number(svc?.UnitCost ?? svc?.unitCost ?? 0);
    return Number.isFinite(total) ? total > 0 : unitCost > 0;
  });

  const normalizedOtherServices = (isDepositInvoice && !hasDetailedRows && Number.isFinite(amountNum) && amountNum > 0)
    ? [{
        Title: `Upfront deposit for Order ${d.orderId || ''}`.trim(),
        UnitCost: amountNum,
        Quantity: 1,
        Total: amountNum,
      }]
    : otherServices;
  const adjustments = Array.isArray(d.adjustments) ? d.adjustments : [];

  const billingAddress = normalizeAddress(d.billingAddress);
  const shippingAddress = normalizeAddress(d.shippingAddress);

  const nowIso = new Date().toISOString();

  return {
    id: d.id ?? "TEMP-INV",
    invoiceStatus: d.invoiceStatus ?? "Open",
    orderId: d.orderId ?? "TEMP-ORDER",
    currency: String(d.currency || 'CAD').toUpperCase(),
    tax: d.tax || null, // { rate, countryCode, stateCode } if server supplies it
    taxRate: d.taxRate != null ? Number(d.taxRate) : undefined,
    billingAddress,
    shippingAddress,
    items: normalizedItems,
    otherServices: normalizedOtherServices,
    adjustments,
    inHandsDate: d.inHandsDate ?? nowIso,
    ...d, // keep anything else that existed
  };
};

// ──────────────────────────────────────────────────────────────
// Cents-based calculator, mirroring Sales Order philosophy
// subtotal = products (units + decos + artwork) + otherServices
// tax      = subtotal * effectiveTaxRate
// total    = subtotal + tax
// ──────────────────────────────────────────────────────────────
function safeNum(n, def = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v : def;
}

function computeTotalsFromInvoice(inv) {
  if (!inv || typeof inv !== 'object') {
    return {
      subtotalCents: 0,
      taxCents: 0,
      totalCents: 0,
      units: 0,
      effectiveTaxRate: 0,
      taxCountryCode: '',
      taxStateCode: '',
    };
  }

  const items = Array.isArray(inv.items) ? inv.items : [];
  const otherServices = Array.isArray(inv.otherServices) ? inv.otherServices : [];

  const canonicalSubtotal = Number(inv?.subtotal);
  const canonicalTax = Number(inv?.taxAmount);
  const canonicalTotal = Number(inv?.total ?? inv?.amount);
  if (Number.isFinite(canonicalSubtotal) && Number.isFinite(canonicalTax) && Number.isFinite(canonicalTotal)) {
    const subtotalCents = toCents(canonicalSubtotal);
    const taxCents = toCents(canonicalTax);
    const totalCents = toCents(canonicalTotal);
    const effectiveTaxRate = subtotalCents > 0 ? (taxCents / subtotalCents) : Number(inv?.tax?.rate || inv?.taxRate || 0);
    return {
      subtotalCents,
      taxCents,
      totalCents,
      units: 0,
      effectiveTaxRate: Number.isFinite(effectiveTaxRate) ? effectiveTaxRate : 0,
      taxCountryCode: inv?.tax?.countryCode || '',
      taxStateCode: inv?.tax?.stateCode || '',
    };
  }

  // Prefer invoice-level tax when provided; fall back to first item with a rate; else 0
  let effectiveTaxRate =
    inv?.tax?.rate != null ? Number(inv.tax.rate) :
    inv?.taxRate != null ? Number(inv.taxRate) :
    null;

  if (effectiveTaxRate == null) {
    const firstWithRate = items.find(p => p?.taxDetails?.rate != null);
    effectiveTaxRate = firstWithRate ? Number(firstWithRate.taxDetails.rate) : 0;
  }
  if (!Number.isFinite(effectiveTaxRate)) effectiveTaxRate = 0;

  const taxCountryCode =
    (inv?.tax?.countryCode) ||
    (items.find(p => p?.taxDetails?.countryCode)?.taxDetails?.countryCode) ||
    '';

  const taxStateCode =
    (inv?.tax?.stateCode) ||
    (items.find(p => p?.taxDetails?.stateCode)?.taxDetails?.stateCode) ||
    '';

  let subtotalCents = 0;
  let units = 0;

  for (const product of items) {
    const productStartCents = subtotalCents;
    const pricings = Array.isArray(product?.pricings) ? product.pricings : [];
    const artworks = Array.isArray(product?.artworks) ? product.artworks : [];
    const decorations = Array.isArray(product?.decorations) ? product.decorations : [];

    // Units + unit line total (prefer Total on each pricing row; fallback Retail * Quantity)
    for (const pr of pricings) {
      const qty = safeNum(pr?.Quantity, 0);
      const retail = safeNum(pr?.Retail, 0);
      const rowTotal = pr?.Total != null ? safeNum(pr.Total, retail * qty) : retail * qty;
      units += qty;
      subtotalCents += toCents(rowTotal);
    }

    // Sales Order modes: a bundled charge belongs in the selling price,
    // whereas Subtract From Margin never contributes invoice revenue.
    let includedChargeCents = 0;
    for (const deco of decorations) {
      const charges = Array.isArray(deco?.Charge) ? deco.Charge : [];
      for (const ch of charges) {
        const flags = resolveAdditionalCostFlags(ch);
        if (flags.subtractFromMargin || !flags.displayToClient) continue;
        const chQty = safeNum(ch?.Quantity, 0);
        const chRetail = safeNum(ch?.Retail, 0);
        const fallbackTotal = ch?.Total != null ? safeNum(ch.Total, chRetail * chQty) : chRetail * chQty;
        const chTotal = chRetail > 0 ? chRetail * chQty : fallbackTotal;
        if (flags.includeInPrice) includedChargeCents += toCents(chTotal);
        else subtotalCents += toCents(chTotal);
      }
    }
    const storedSubtotal = Number(product?.Subtotal ?? product?.subtotal);
    const currentVisibleCents = subtotalCents - productStartCents;
    const sourceAlreadyEmbedsIncludes = Number.isFinite(storedSubtotal) &&
      Math.abs(toCents(storedSubtotal) - currentVisibleCents) <= 1;
    if (!sourceAlreadyEmbedsIncludes) subtotalCents += includedChargeCents;

    // Artworks: two common shapes
    //  1) flat Charge per unit (multiply by item quantity)
    //  2) explicit Total on each artwork row
    // We’ll try explicit Total first, else Charge * matched pricing qty, else 0.
    for (let i = 0; i < artworks.length; i++) {
      const art = artworks[i] || {};
      if (art?.Total != null) {
        subtotalCents += toCents(safeNum(art.Total, 0));
        continue;
      }
      const charge = safeNum(art?.Charge, 0);
      const matchedQty = safeNum(pricings?.[i]?.Quantity, 0);
      subtotalCents += toCents(charge * matchedQty);
    }
  }

  // Other Services
  // Prefer subtotal-like keys first (exclusive of tax), then fallback to UnitCost * Quantity,
  // and only use Total if subtotal cannot be resolved.
  for (const svc of otherServices) {
    const subtotalNoTax =
      svc?.Subtotal != null ? safeNum(svc.Subtotal, 0) :
      svc?.subTotal != null ? safeNum(svc.subTotal, 0) :
      svc?.subtotal != null ? safeNum(svc.subtotal, 0) :
      null;
    const svcTaxPctRaw = Number(svc?.Taxes);
    const svcTaxRate = Number.isFinite(svcTaxPctRaw) ? (svcTaxPctRaw / 100) : 0;
    const total = subtotalNoTax != null
      ? subtotalNoTax
      : (
        svc?.Total != null
          ? (
            svcTaxRate > 0
              ? safeNum(svc?.Total, 0) / (1 + svcTaxRate)
              : safeNum(svc?.Total, 0)
          )
          : (
            svc?.UnitCost != null || svc?.Quantity != null
              ? safeNum(svc?.UnitCost, 0) * safeNum(svc?.Quantity, 0)
              : 0
          )
      );
    subtotalCents += toCents(total);
  }

  const taxCents = Math.round(subtotalCents * effectiveTaxRate);
  const totalCents = subtotalCents + taxCents;

  return {
    subtotalCents,
    taxCents,
    totalCents,
    units,
    effectiveTaxRate,
    taxCountryCode,
    taxStateCode,
  };
}

// ──────────────────────────────────────────────────────────────

const GeneratedInvoice = () => {
  const { id } = useParams();
  const { authState } = useContext(AuthContext) || {};

  const isInternalUser =
    authState?.status === true ||
    Boolean(authState?.role && authState.role !== 'unknown');
  const [loading, setLoading] = useState(true);
  const [overlay, setOverlay] = useState(true);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [visualAssetsReady, setVisualAssetsReady] = useState(false);
  const [paymentReadiness, setPaymentReadiness] = useState({ status: 'loading' });
  const [orderContextTotals, setOrderContextTotals] = useState(null);
  const [orderContextSource, setOrderContextSource] = useState(null);
  const [instanceUniqueId, setInstanceUniqueId] = useState(null);
  const { headerConfig, footerAddress, termsHtml } = useProjectTemplateConfig({
    instanceUniqueId,
    viewKey: 'depositInvoice',
  });

  // Wait for the visible document's fonts and brand imagery before its first reveal.
  // Image errors and slow third-party hosts cannot block the invoice indefinitely.
  useEffect(() => {
    if (!invoiceDetails || loading || loadError) {
      setVisualAssetsReady(false);
      return undefined;
    }
    let cancelled = false;
    let timeoutId;
    setVisualAssetsReady(false);
    const imageUrls = [
      headerConfig?.logoUrl || 'https://elitepromomarketing.com/wp-content/uploads/2024/08/logo-1-600x183.png',
      headerConfig?.backgroundUrl,
    ].filter(Boolean);
    const loadImage = (url) => new Promise((resolve) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = resolve;
      image.src = url;
      if (image.complete) resolve();
    });
    const fontsReady = document.fonts?.ready
      ? document.fonts.ready.catch(() => {})
      : Promise.resolve();
    Promise.race([
      Promise.all([fontsReady, ...imageUrls.map(loadImage)]),
      new Promise((resolve) => { timeoutId = window.setTimeout(resolve, 4000); }),
    ]).then(() => {
      if (!cancelled) setVisualAssetsReady(true);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [invoiceDetails, loading, loadError, headerConfig?.logoUrl, headerConfig?.backgroundUrl]);

  const getEstimation = async ({ useOverlay = false } = {}) => {
    try {
      if (useOverlay) setOverlay(true);
      setLoading(true);
      setLoadError('');
      // Clear stale financials immediately: neither a failed request nor a
      // slow server should leave visible $0.00 or a payment button behind.
      setInvoiceDetails(null);
      setPaymentReadiness({ status: 'loading' });
      setOrderContextTotals(null);
      const response = await getPublicInvoice(id);
      const raw = response?.data;
      if (!raw || typeof raw !== 'object' || raw.id == null) {
        throw new Error('Invoice details could not be loaded. Please retry.');
      }
      const hydrated = applyInvoiceDefaults(raw);
      const computed = computeTotalsFromInvoice(hydrated);
      const token = new URLSearchParams(window.location.search || '').get('prt');
      let readiness = { status: 'unsigned' };
      if (token) {
        try {
          const paymentResponse = await axios.get(
            process.env.REACT_APP_NODEJS_SERVER_URL +
              '/invoices/public/' + encodeURIComponent(raw.id) + '/payment-readiness',
            { params: { prt: token } },
          );
          readiness = paymentResponse?.data || { status: 'error' };
        } catch (error) {
          readiness = {
            status: 'error',
            message: error?.response?.data?.message ||
              'Unable to verify payment options. Please retry or contact Accounting.',
          };
        }
      }
      const financialError = validatePublicInvoiceFinancials(hydrated, computed, readiness);
      if (financialError) throw new Error(financialError);
      setInvoiceDetails(hydrated);
      setPaymentReadiness(readiness);
    } catch (error) {
      const message = error?.response?.data?.message ||
        error?.message || 'Failed to load invoice.';
      setLoadError(message);
      setInvoiceDetails(null);
      setPaymentReadiness({ status: 'error' });
    } finally {
      setLoading(false);
      setOverlay(false);
    }
  };

    const handleComments = async (estimationId, productId, comment) => {
    try {
      const payload = { estimationId, productId, comment };
      setLoading(true);
      // await updateComment(payload)
      await getEstimation();
      toast.success('Your comment has been posted successfully.');
    } catch (error) {
      toast.error('failed to post comment');
    } finally {
      setLoading(false);
      setOverlay(false);
    }
  };

  const handleAgree = async () => {
    try {
      const payload = {
        status: 2 || invoiceDetails?.status,
        customerAgreed: 1,
        orderId: invoiceDetails?.orderId,
      };
      setLoading(true);
      // await updateEstimation(invoiceDetails?.estimationId, payload)
      await getEstimation();
      toast.success(
        'Thank you for agreeing to the estimation. We look forward to moving forward with this order!'
      );
    } catch (error) {
      toast.error('Failed to update the agreement');
    } finally {
      setLoading(false);
      setOverlay(false);
    }
  };

  useEffect(() => {
    if (id) getEstimation({ useOverlay: true });
  }, [id]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const originalTitle = document.title;
    const invoiceTitle = invoiceDetails?.id ? `Invoice #${invoiceDetails.id}` : 'Invoice';
    let titleRestoreTimer = null;

    const suppressBrowserPrintTitle = () => {
      document.title = '';
    };

    const restoreBrowserTitle = () => {
      if (titleRestoreTimer) window.clearTimeout(titleRestoreTimer);
      titleRestoreTimer = window.setTimeout(() => {
        document.title = originalTitle;
      }, 0);
    };

    const handlePrintShortcut = (event) => {
      const isPrintShortcut = (event.ctrlKey || event.metaKey) && String(event.key || '').toLowerCase() === 'p';
      if (!isPrintShortcut || !invoiceDetails?.id || !isInternalUser) return;

      event.preventDefault();
      event.stopPropagation();
      printInvoice(invoiceDetails.id).catch(() => {
        document.title = invoiceTitle;
        window.print();
      });
    };

    window.addEventListener('beforeprint', suppressBrowserPrintTitle);
    window.addEventListener('afterprint', restoreBrowserTitle);
    window.addEventListener('keydown', handlePrintShortcut, true);

    return () => {
      if (titleRestoreTimer) window.clearTimeout(titleRestoreTimer);
      document.title = originalTitle;
      window.removeEventListener('beforeprint', suppressBrowserPrintTitle);
      window.removeEventListener('afterprint', restoreBrowserTitle);
      window.removeEventListener('keydown', handlePrintShortcut, true);
    };
  }, [invoiceDetails?.id, isInternalUser]);

  useEffect(() => {
    const orderId = invoiceDetails?.orderId || invoiceDetails?.OrderId;
    if (!orderId) return;
    let alive = true;
    const loadInstance = async () => {
      try {
        const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`;
        const res = await axios.get(url);
        const order = res?.data?.order || {};
        try {
          const rel = parseRel(invoiceDetails?.invoiceRelationships);
          const byOrderBusinessId = String(rel?.orderId || orderId || '').trim();
          const soIdExact = Number(rel?.salesOrderId || 0);
          let source = 'invoice-fallback';
          let sourceKey = null;
          let so = null;

          if (soIdExact > 0) {
            const byIdRes = await axios.get(
              `${process.env.REACT_APP_NODEJS_SERVER_URL}/estimations/${soIdExact}`
            );
            so = byIdRes?.data?.salesOrder || byIdRes?.data || null;
            if (so?.estimationId || so?.id) {
              source = 'salesOrderId-exact';
              sourceKey = soIdExact;
            }
          }

          if (!so && byOrderBusinessId) {
            so = await fetchLatestCompletedSalesOrderForOrder(byOrderBusinessId);
            if (so?.estimationId || so?.id) {
              source = 'salesOrder-by-business-orderId';
              sourceKey = byOrderBusinessId;
            }
          }

          const soTotals = so ? computeTotalsFromSO(so) : null;
          let soSubtotal = Number(soTotals?.subtotalCents || 0) / 100;
          let soTotal = Number(soTotals?.totalCents || 0) / 100;
          const stamp = so?.stampedBookedAmount && typeof so.stampedBookedAmount === 'string'
            ? (() => { try { return JSON.parse(so.stampedBookedAmount); } catch { return null; } })()
            : so?.stampedBookedAmount;
          const stampedRevenue = Number(stamp?.targetRevenue || 0);
          if (stampedRevenue > 0) {
            soSubtotal = stampedRevenue;
            const taxRate = Number(so?.tax?.rate ?? 0);
            if (taxRate > 0) soTotal = stampedRevenue * (1 + taxRate);
          }

          if (alive && soSubtotal > 0 && soTotal > 0) {
            setOrderContextTotals({ subtotal: soSubtotal, totalInclTax: soTotal });
            setOrderContextSource({ source, sourceKey });
          } else if (alive) {
            setOrderContextTotals(null);
            setOrderContextSource({ source: 'invoice-fallback', sourceKey: null });
          }
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[GeneratedInvoice.orderContextTotals]', {
              invoiceId: invoiceDetails?.id || null,
              source,
              sourceKey,
              subtotal: soSubtotal,
              totalInclTax: soTotal,
            });
          }
        } catch {
          if (alive) {
            setOrderContextTotals(null);
            setOrderContextSource({ source: 'invoice-fallback', sourceKey: null });
          }
        }
        const raw = order?.InstanceUniqueIds;
        let resolved = null;
        if (Array.isArray(raw)) {
          resolved = raw[0] || null;
        } else if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            resolved = Array.isArray(parsed) ? parsed[0] : parsed;
          } catch {
            resolved = raw;
          }
        }
        if (alive) setInstanceUniqueId(resolved);
      } catch (err) {
        if (alive) setInstanceUniqueId(null);
        if (alive) setOrderContextTotals(null);
      }
    };
    loadInstance();
    return () => {
      alive = false;
    };
  }, [invoiceDetails?.orderId, invoiceDetails?.OrderId, invoiceDetails?.invoiceRelationships, invoiceDetails?.id]);

  // Map other services to subtotal/tax/total rows (same derivation as GeneratedSalesOrder)
  const otherServices = Array.isArray(invoiceDetails?.otherServices)
    ? invoiceDetails.otherServices.map((service) => {
        const amounts = resolveOtherServiceCustomerAmounts(service);

        return {
          item: service?.Title ?? 'Other Service',
          quantity: amounts.quantity,
          subtotal: amounts.subtotal,
          tax: amounts.tax,
          total: amounts.total,
          taxRate: amounts.taxRate,
        };
      })
    : [];

  // Unified totals (mirrors Sales Order approach)
  const totalsCents = useMemo(
    () => (invoiceDetails ? computeTotalsFromInvoice(invoiceDetails) : null),
    [invoiceDetails]
  );
  const taxBreakdown = useMemo(
    () => (invoiceDetails && totalsCents
      ? buildInvoiceTaxBreakdown(invoiceDetails, { targetTaxCents: totalsCents.taxCents })
      : []),
    [invoiceDetails, totalsCents]
  );
  const grandTotal = totalsCents ? fromCents(totalsCents.totalCents) : 0;
  // Presentation-only tax-cent allocation; canonical saved financials remain untouched.
  const invoiceDisplayPlan = useMemo(() => buildInvoiceDisplayPlan({
    invoice: invoiceDetails || {},
    services: otherServices,
    totalsCents,
    taxBreakdown,
  }), [invoiceDetails, otherServices, totalsCents, taxBreakdown]);

  return (
    <>
      <Box className={styles.rootContainer}>
        <InvoicePrintActions
          ready={!loading && !overlay && !loadError && Boolean(invoiceDetails)}
          isInternalUser={isInternalUser}
          orderId={invoiceDetails?.orderId}
          instanceUniqueId={instanceUniqueId}
          invoiceId={invoiceDetails?.id}
        />
        <Container className={styles.printDocument} maxWidth="lg" data-invoice-print-document>
          {(loading || overlay || (!loadError && (!invoiceDetails || !visualAssetsReady))) && (
            <Box className={styles.invoiceLoadingScreen} role="status" aria-live="polite" aria-label="Loading invoice">
              <CircularProgress size={34} thickness={3.5} className={styles.invoiceLoadingSpinner} />
            </Box>
          )}
          {!loading && !overlay && loadError && (
            <Box role="alert" sx={{ p: 3, mx: 'auto', my: 5, maxWidth: 560, border: '1px solid #fee2e2', borderRadius: 2 }}>
              <Typography variant="h6" color="error" gutterBottom>Invoice temporarily unavailable</Typography>
              <Typography sx={{ mb: 2 }}>{loadError}</Typography>
              <Button variant="contained" onClick={() => getEstimation({ useOverlay: true })}>
                Retry loading invoice
              </Button>
            </Box>
          )}
          {!loading && !overlay && !loadError && invoiceDetails && (
            <Box className={visualAssetsReady ? styles.invoiceFadeIn : styles.invoiceBeforeReveal}>
              {invoiceDetails?.invoiceStatus === 'Completed' && <Watermark />}

              <Header invoiceDetails={invoiceDetails} headerConfig={headerConfig} />
          <InvoiceDetails invoiceDetails={invoiceDetails} finalOrderTotal={grandTotal} />

          <Box className={styles.PresentationContainer}>
            <Grid container spacing={2} className={styles.presentationCard}>
              {invoiceDetails?.items?.map((data, index) => (
                <Grid item xs={12} key={index} className={styles.presentationGrid}>
                  <Invoice
                    ls
                    productData={data}
                    displayRows={invoiceDisplayPlan[index]}
                    currency={invoiceDetails?.currency}
                    estimationId={invoiceDetails?.estimationId}
                    handleComments={handleComments}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {otherServices?.length > 0 && (
            <TotalContainer otherServices={otherServices} invoiceDetails={invoiceDetails} />
          )}

          {invoiceDetails?.adjustments?.length > 0 && (
            <AdjustmentContainer
              adjustments={invoiceDetails.adjustments}
              currency={invoiceDetails?.currency || 'CAD'}
            />
          )}

          <Footer
            invoiceDetails={invoiceDetails}
            authState={authState}
            totalsCents={totalsCents}
            taxBreakdown={taxBreakdown}
            orderContextTotals={orderContextTotals}
            footerAddress={footerAddress}
            termsHtml={termsHtml}
            onRefreshInvoice={() => getEstimation({ useOverlay: false })}
            paymentReadiness={paymentReadiness}
          />
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default GeneratedInvoice;
